<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Task" :avatarImage="avatarImage" :avatarCircleMode="false" :loading="pageDataLoading">
          <h4>Task #{{task.sequenceNumber}}</h4>
          <h4>{{task.name}}</h4>

          <OverviewBoxInfoItem icon="fa-calendar">
            {{formatDate(task.scheduleStartDate)}}
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-clock">
            {{formatTime(task.scheduleStartDate)}}
          </OverviewBoxInfoItem>

          <OverviewBoxGroup v-if="task.labels && task.labels.length > 0" title="Labels">
            <LabelTags :labelList="task.labels" outlineStyle />
          </OverviewBoxGroup>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="Type">
              <LabelBadge :labelColour="task.taskType.highlightColour">{{task.taskType.name}}</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Status">
              <LabelBadge :labelClass="`label-task-status-${task.status}`">{{task.status | formatLookupName(TaskStatusTypeLookup)}}</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Number" v-if="task.sequenceNumber">{{task.sequenceNumber}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Resources" v-if="task.resources">
              <template v-for="(resource, resourceIndex) of task.resources">
                <br v-if="resourceIndex > 0" :key="`sep-${resource.id}`" />
                <div class="float-right" :key="`resource-${resource.id}`">{{resource.name}}</div>
              </template>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Users" v-if="task.users">
              <template v-for="(user, userIndex) of task.users">
                <br v-if="userIndex > 0" :key="`sep-${user.id}`" />
                <div class="float-right" :key="`resource-${user.id}`">{{user.name}}</div>
              </template>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Created On">{{task.createdAt | formatDate}}</OverviewBoxListItem>

            <OverviewBoxListItem title="Last Updated">{{task.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

      </div>

      <div class="col-sm-10">
        <TabbedBox :loading="pageDataLoading">
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-task-summary">
              <i class="far fa-calendar"></i> Summary
            </Tab>
            <Tab ref="pendingPanelsTab" tabid="tab-pending-panels" v-if="isTaskPendingPanels">
              <i class="far fa-layer-group"></i> Pending Panels
            </Tab>
            <Tab ref="itemsTab" tabid="tab-task-items" v-if="containsTaskItems">
              <i class="far fa-clipboard-list-check"></i> Runsheet
            </Tab>
            <Tab tabid="tab-scanned-items" v-if="isTaskDespatchEnabled">
              <i class="far fa-scanner"></i> Scanned Items
            </Tab>
            <Tab tabid="tab-task-map" @show="onMapDisplayed" v-if="isTaskLocationTracking">
              <i class="far fa-map-marker-alt"></i> Map
            </Tab>
            <Tab tabid="tab-task-documents">
              <i class="far fa-book"></i> Documents
            </Tab>
            <Tab tabid="tab-task-activity-log">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>

          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-task-summary">
              <div class="row">
                <div class="col-12">
                  <ActionDropMenu class="float-right">
                    <DropdownMenuItem v-if="canEditTask" icon="fa-pencil" @click="editTask">Edit Task</DropdownMenuItem>
                    <DropdownMenuItem v-if="canRecheckApprovals" icon="fa-sync" @click="recheckApprovals">Re-check Approvals</DropdownMenuItem>
                    <DropdownMenuDivider v-if="canEditTask && (canPrintRunSheet || canPrintCustomerSummarySheet || canSkipPendingPanels)" />
                    <DropdownMenuItem v-if="canPrintRunSheet" icon="fa-print" @click="printRunSheet()">Print Runsheet</DropdownMenuItem>
                    <DropdownMenuItem v-if="canPrintCustomerSummarySheet" icon="fa-print" @click="printCustomerSummarySheet()">Print Customer {{task.taskType.name}} Summary</DropdownMenuItem>
                    <DropdownMenuItem @click="skipPendingPanels" icon="fa-forward" v-if="canSkipPendingPanels">Skip Pending Production</DropdownMenuItem>
                    <DropdownMenuDivider v-if="canCompleteTask || canCancelTask" />
                    <DropdownMenuItem @click="completeTask" icon="fa-check-circle" v-if="canCompleteTask">Close Task</DropdownMenuItem>
                    <DropdownMenuItem @click="cancelTask" icon="fa-times" v-if="canCancelTask">Cancel Task</DropdownMenuItem>
                  </ActionDropMenu>

                  <ProgressTracker v-if="task.id">
                    <ProgressTrackerItem title="Task Created" :completed="true" :cancelled="isTaskCancelled" icon="fa-list-alt" :timestamp="task.createdAt">
                      <template v-slot:completed>
                        <div class="row">
                          <div class="col-lg-2">
                            <h5 class="mb-0">CREATED BY</h5>
                            {{task.requestUser.name}}
                            <h5 class="mt-2 mb-0">NAME</h5>
                            {{task.name}}
                          </div>
                          <div class="col-lg-2">
                            <h5 class="mt-2 mb-0">DETAILS</h5>
                            <span v-if="task.description" v-html="convertNewlinesToBreaks(task.description)"></span>
                          </div>
                        </div>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="isTaskDespatchEnabled"
                      :current="task.status == TaskStatusTypes.pendingProduction"
                      :completed="isTaskPendingApproval || isTaskLoading || isTaskReady || isTaskInProgress || isTaskCompleted"
                      title="Pending Production"
                      icon="fa-sync"
                      :timestamp="task.startedDate"
                      :cancelled="isTaskCancelled">

                      <template v-slot:current>
                        <p>Waiting for the production items to complete production</p>

                        <ProgressTrackerButton @click="displayPendingPanelsTab" icon="fa-list-ol">View Panels</ProgressTrackerButton>
                        &nbsp;&nbsp;
                        <ProgressTrackerButton @click="skipPendingPanels" icon="fa-forward" v-if="canSkipPendingPanels">Skip Pending Production</ProgressTrackerButton>
                      </template>
                      <template v-slot:completed>
                        <p>Items have completed production</p>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="requiresAccountsApproval"
                      :current="task.status == TaskStatusTypes.pendingApproval"
                      :completed="isTaskPendingPanels || isTaskLoading || isTaskReady || isTaskInProgress || isTaskCompleted"
                      title="Pending Approval"
                      icon="fa-thumbs-up"
                      :timestamp="task.startedDate"
                      :cancelled="isTaskCancelled">

                      <template v-slot:current>
                        <p>The task is waiting for approval.</p>
                        <p>Once the task has been approved, the task will move to the next stage</p>
                        <br />
                        <TaskApprovalBar
                          v-if="requiresAccountsApproval"
                          title="Accounts Approval"
                          :approvalType="TaskApprovalTypes.account"
                          :task="task"
                          :permissionType="PermissionTypes.TaskPermissionTypes.taskAccountsApproval"
                          @approve="approveTask(TaskApprovalTypes.account, 'Account')"
                          @deny="denyTask(TaskApprovalTypes.account, 'Account')"
                          class="mt-2"
                        />
                      </template>
                      <template v-slot:completed>
                        <TaskApprovalBar
                          v-if="requiresAccountsApproval"
                          title="Accounts Approval"
                          :approvalType="TaskApprovalTypes.account"
                          :task="task"
                          :permissionType="PermissionTypes.TaskPermissionTypes.taskAccountsApproval"
                          class="mt-2"
                        />
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="task.taskRequiresLoading"
                      :current="task.status == TaskStatusTypes.loading"
                      :completed="isTaskReady || isTaskInProgress || isTaskCompleted"
                      title="Loading"
                      :icon="isTaskLocationTracking ? 'fa-truck-loading' : 'fa-tasks'"
                      note="Waiting for loading to start"
                      :timestamp="task.loadedDate"
                      :cancelled="isTaskCancelled">

                      <template v-slot:waiting>
                        <TaskLoadingSummary :task="task" :taskItemScannedItemList="taskItemScannedItemList" />
                      </template>
                      <template v-slot:current>
                        <p>The order items are currently being loaded</p>
                        <TaskLoadingSummary :task="task" :taskItemScannedItemList="taskItemScannedItemList" />
                      </template>
                      <template v-slot:completed>
                        <p>Loading has been completed</p>
                        <ProgressTrackerPanel v-if="isTaskLoadingNotesRequired" title="Loaded Notes">
                          Note: {{task.loadingNotes}}<br /><br />
                          <span v-if="task.inventoryBin">Bin: {{task.inventoryBin.code}}</span>
                          <br />
                          <template v-slot:notes>
                            Loading completed on {{formatDateTime(task.loadedDate)}}
                          </template>
                        </ProgressTrackerPanel>
                        <FormHorizontalRule v-if="isTaskLoadingNotesRequired" />
                        <ProgressTrackerPanel v-if="task.isPartialScanIn" title="Partial Scan In">
                          <p>Task partially scanned in with reason:</p>
                          <LabelTags :labelList="task.partialScanInLabels" />
                          <template v-slot:notes>
                            Loading completed on {{formatDateTime(task.loadedDate)}}
                          </template>
                        </ProgressTrackerPanel>
                        <FormHorizontalRule v-if="task.isPartialScanIn" />
                        <TaskLoadingSummary :task="task" :taskItemScannedItemList="taskItemScannedItemList" />
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      :current="task.status == TaskStatusTypes.ready"
                      :completed="isTaskInProgress || isTaskCompleted"
                      title="Ready"
                      note="Waiting for task to begin"
                      :icon="isTaskLocationTracking ? 'fa-truck-loading' : 'fa-tasks'"
                      :timestamp="task.startedDate"
                      :cancelled="isTaskCancelled">

                      <template v-slot:current>
                        <p v-if="isTaskLocationTracking">Currently waiting to begin</p>
                        <p v-if="!isTaskLocationTracking">Waiting to be completed</p>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      :current="task.status == TaskStatusTypes.inProgress"
                      :completed="isTaskCompleted"
                      :title="`${task.taskType.name} In Progress`"
                      :icon="getTaskTypeIcon(task.taskType)"
                      note="Waiting for task to start"
                      :timestamp="task.deliveryStartedDate"
                      :cancelled="isTaskCancelled">

                      <template v-slot:all>
                        <TaskProgressPreview :task="task" :taskItemScannedItemList="taskItemScannedItemList" />
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="!isTaskCancelled"
                      title="Completed"
                      :completed="isTaskCompleted"
                      :cancelled="isTaskCancelled"
                      icon="fa-check"
                      note="Waiting for task to be completed"
                      :timestamp="task.completedDate">

                      <template v-slot:completed>
                        <p>Completed</p>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="isTaskCancelled"
                      title="Task Cancelled"
                      :cancelled="true"
                      :timestamp="task.cancelledDate">
                      <template v-slot:cancelled>
                        <div class="row">
                          <div class="col-lg-12">
                            <h5>CANCELLED</h5>
                            {{task.cancelledReason}}
                          </div>
                        </div>
                      </template>
                    </ProgressTrackerItem>
                  
                  </ProgressTracker>
                </div>
              </div>

            </TabPage>

            <TabPage tabid="tab-pending-panels">
              <div class="row">
                <div class="col-12">
                  <ActionDropMenu class="float-right">
                    <DropdownMenuItem @click="printPendingPanelSheet()" icon="fa-print">Print Panel Report</DropdownMenuItem>
                  </ActionDropMenu>

                  <ContentTitle>Pending Panel List</ContentTitle>
                  <ContentSubTitle>The following panels require completion in production for despatch</ContentSubTitle>
                </div>
              </div>

              <div class="row mt-2">
                <div class="col-12">
                  <StaticDataTable class="static-table-list">
                    <template v-slot:header>
                      <tr>
                        <th>Barcode</th>
                        <th>Status</th>
                        <th>Mark</th>
                        <th>Size</th>
                        <th>Description</th>
                        <th>Order</th>
                        <th>Customer</th>
                        <th>Current Stage</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="panel of taskPendingPanels" :key="panel.id">
                        <td><LinkProductionPanel :id="panel.id" :name="panel.barcode" /></td>
                        <td>
                          <LabelPanelStatusBadge :status="panel.status" />
                          <LabelBadge labelClass="label-order-type-nonChargeableRework" class="ml-1" v-if="panel.isRecut">Recut</LabelBadge>
                        </td>
                        <td>{{formatHyphenEmptyValue(panel.orderItem.mark)}}</td>
                        <td>{{panel.panelHeight}} x {{panel.panelWidth}}</td>
                        <td>{{orderItemDescription(panel.orderItem)}}</td>
                        <td><LinkOrder :id="panel.orderId" :name="panel.order.sequenceNumber" /></td>
                        <td>{{panel.order.contact.name}}</td>
                        <td>{{panel.currentTaskName}}</td>
                      </tr>
                    </template>
                    <template v-slot:empty v-if="taskPendingPanels.length == 0">
                      No linked panels waiting to complete production
                    </template>
                  </StaticDataTable>
                </div>
              </div>
            </TabPage>


            <TabPage tabid="tab-task-items">
              <DataPageArea :title="`${task.taskType.name} Runsheet`" subTitle="Items in the runsheet for the task">
                <template slot="actionArea">
                  <ActionDropMenu class="float-right">
                    <DropdownMenuItem v-if="canEditTask && !taskItemList.editing" icon="fa-pencil" @click="editTaskItemOrder()">Edit Item Order</DropdownMenuItem>
                    <DropdownMenuDivider v-if="canEditTask && !taskItemList.editing" />
                    <DropdownMenuItem v-if="canEditTask && !taskItemList.editing" icon="fa-stopwatch" @click="addTaskItemStoppage()">Add Stoppage</DropdownMenuItem>
                    <DropdownMenuItem v-if="canEditTask && !taskItemList.editing" icon="fa-plus-circle" @click="addTaskItemGeneralItem()">Add General Item</DropdownMenuItem>
                    <DropdownMenuDivider v-if="canPrintRunSheet && canEditTask && !taskItemList.editing" />
                    <DropdownMenuItem v-if="canPrintRunSheet && !taskItemList.editing" icon="fa-print" @click="printRunSheet()">Print Runsheet</DropdownMenuItem>
                  </ActionDropMenu>
                </template>
                <template slot="dataArea">
                  <DataItem title="Start Location" v-if="isTaskLocationTracking">{{startLocationDescription}}</DataItem>
                  <DataItem title="End Location" v-if="isTaskLocationTracking">{{endLocationDescription}}</DataItem>
                </template>
              </DataPageArea>

              <div class="row mt-3">
                <div class="col-lg-12">
                  <ContentTitle>Items</ContentTitle>

                  <FormGroup :labelColumnSize="6" :form="taskItemList.form">
                    <TaskItemList
                      :task="task"
                      :taskType="task.taskType"
                      :value="task.taskItems"
                      :editingOrder="taskItemList.editing"
                      :showActions="canEditTaskItems"
                      :showStatus="true"
                      @sortUpdated="taskItemList.sortOrder = $event"
                      @editTaskItem="editRunsheetItem"
                      @cancelTaskItem="cancelRunsheetItem"
                      @completeTaskItem="completeTaskItem"
                      />

                    <div v-if="taskItemList.editing">
                      <FormHorizontalRule />
                      <FormSubmit label="Update" :submitting="taskItemList.submitting" @cancelled="cancelTaskItemListEditing" @submitted="submitTaskItemListEditing"></FormSubmit>
                    </div>
                  </FormGroup>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-scanned-items">
              <div class="row">
                <div class="col-12">
                  <ContentTitle>Scanned Items</ContentTitle>
                  <ContentSubTitle>List of scanned order items during loading / unloading</ContentSubTitle>
                </div>
              </div>

              <TaskItemScannedList
                :task="task"
                :taskScannedItemList="taskScannedItemList"
              />
            </TabPage>

            <TabPage tabid="tab-task-map" v-if="isTaskLocationTracking">
              <TaskManagerMapView 
                ref="mapView"
                v-if="containsMappingData"
                :displayRoutes="false"
                :displayUserLocations="true"
                :displayOrders="false"
                :adjustBoundsOnLoad="false"
                :mapUserFilter="taskUserMapFilter"
                :mapTypeControl="true"
                @userLocationUpdated="userLocationUpdated">

                <template slot-scope="{ google, map, mapState }">
                  <TaskMapRoute
                    routeColour="#C7E4FF"
                    :polylineCode="task.routePolylineCode"
                    :strokeWeight="5"
                    :itemPoints="task.routeKeyPoints"
                    :google="google"
                    :map="map"
                    />

                  <template v-for="taskUserRoute of task.taskUserRoutes">
                    <TaskMapRoute
                      ref="mapTaskUserRoute"
                      :key="taskUserRoute.id"
                      :routeColour="taskUserRoute.highlightColour"
                      :strokeWeight="2.0"
                      :polylineCode="taskUserRoute.routePolylineCode"
                      :google="google"
                      :map="map"
                      />

                    <!-- Route User Events -->
                    <template v-for="routeEvent of taskUserRoute.routeUserEvents">
                      <MapTaskRouteEventMarker
                        :key="routeEvent.id"
                        :position="routeEvent.coordinate"
                        :routeEvent="routeEvent"
                        :google="google"
                        :map="map"
                        :mapState="mapState"
                        @click="onMapRouteEventClicked(routeEvent, taskUserRoute, $event)"
                        />
                    </template>

                  </template>

                  <template v-for="(taskItem, index) of task.taskItems">
                    <MapTaskItemMarker
                      :key="index"
                      v-if="taskItem.isLocationEnabled && taskItem.locationCoordinate"
                      :taskItem="taskItem"
                      :position="taskItem.locationCoordinate"
                      :google="google"
                      :map="map"
                      :mapState="mapState"
                      @click="onMapTaskItemClicked(taskItem, $event)"
                    />
                  </template>

                  <MapTaskEndpointMarker
                    v-if="task.routeStartLocationCoordinate"
                    :isStartPoint="true"
                    :isEndPoint="isRouteStartMatchEnd"
                    :task="task"
                    :position="task.routeStartLocationCoordinate"
                    :google="google"
                    :map="map"
                    :mapState="mapState"
                    @click="onMapTaskStartClicked($event)"
                  />
                  <MapTaskEndpointMarker
                    v-if="task.routeEndLocationCoordinate && !isRouteStartMatchEnd"
                    :isStartPoint="false"
                    :isEndPoint="true"
                    :task="task"
                    :position="task.routeEndLocationCoordinate"
                    :google="google"
                    :map="map"
                    :mapState="mapState"
                    @click="onMapTaskEndClicked($event)"
                  />
                </template>

              </TaskManagerMapView>

              <TaskItemPopover ref="mapTaskItemPopover" :appendToBody="true" />
              <MapTaskEndpointPopover ref="mapTaskEndpointPopover" :appendToBody="true" />

              <MapRouteEventPopover ref="mapRouteEventPopover" :appendToBody="true" />

              <p v-if="!containsMappingData" class="text-center font-italic text-muted">
                No mapping information associated with the task
              </p>
            </TabPage>

            <TabPage tabid="tab-task-documents">
              <div class="row">
                <div class="col-lg-12">
                  <DocumentList
                    title="Documents"
                    :linkType="LinkTypes.task"
                    :linkId="task.id"
                    :permissionResource="PermissionTypes.TaskPermissionTypes.task"
                    class="mt-2" />
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-task-activity-log">
              <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.task" :linkId="task.id">
                <template v-slot:title><h3>Recent Activity</h3></template>
              </ActivityLogStream>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import Utils from '@/utils/utils'
import MapUtils from '@/utils/mapUtils'
import Form from '@/mixins/form'
import ColourMixin from '@/mixins/colour'
import PDFMixin from '@/mixins/pdf'
import TaskManagerMixin from '@/modules/tasks/mixins/taskManagerMixin'
import TaskDespatchMixin from '@/modules/tasks/mixins/taskDespatchMixin'
import GoogleMapMixin from '@/mixins/googleMap'

import ProgressTracker from '@/components/layout/progressTracker.vue'
import ProgressTrackerItem from '@/components/layout/progressTrackerItem.vue'
import ProgressTrackerButton from '@/components/layout/progressTrackerButton.vue'
import ProgressTrackerPanel from '@/components/layout/progressTrackerPanel.vue'

import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'
import DocumentList from '@/components/documentList.vue'
import MapMarker from '@/components/maps/mapMarker'
import MapPolyline from '@/components/maps/mapPolyline'
import TaskMapRoute from '@/modules/tasks/components/taskMapRoute'
import TaskManagerMapView from '@/modules/tasks/components/taskManagerMapView'
import TaskItemPopover from '@/modules/tasks/components/taskItemPopover'
import MapRouteEventPopover from '@/modules/tasks/components/mapRouteEventPopover'
import MapTaskItemMarker from '@/modules/tasks/components/mapTaskItemMarker'
import MapTaskEndpointMarker from '@/modules/tasks/components/mapTaskEndpointMarker'
import MapTaskRouteEventMarker from '@/modules/tasks/components/mapTaskRouteEventMarker'
import MapTaskEndpointPopover from '@/modules/tasks/components/mapTaskEndpointPopover'
import TaskItemList from '@/modules/tasks/components/taskItemList'
import TaskItemScannedList from '@/modules/tasks/components/taskItemScannedList'
import TaskAlertList from '@/modules/tasks/components/taskAlertList'
import LabelPanelStatusBadge from '@/modules/production/components/labelPanelStatusBadge'
import TaskProgressPreview from '@/modules/tasks/components/taskProgressPreview'
import TaskLoadingSummary from '@/modules/tasks/components/taskLoadingSummary'
import TaskApprovalBar from '@/modules/tasks/components/taskApprovalBar'

import TaskEditDialog from '@/modules/tasks/views/taskEditDialog'
import TaskItemEditDialog from '@/modules/tasks/views/taskItemEditDialog'
import TaskItemCompleteDialog from '@/modules/tasks/views/taskItemCompleteDialog'
import CancelTaskDialog from '@/modules/tasks/views/cancelTaskDialog'
import GeneralActionDialog from '@/components/dialog/generalActionDialog'

import { TaskStatusTypes, TaskStatusTypeLookup, ResourceTypes, LinkTypes, TaskApprovalTypes, AccountLabelTypes, ScannedItemTypes, ScannedItemTypeLookup, TaskItemTypes, TaskTypeInvoicingRuleTypes, TaskRouteLocationTypes } from '@/store/types'

export default {
  name: 'Task',
  mixins: [
    ModalDialogMixin,
    Form,
    ColourMixin,
    TaskManagerMixin,
    TaskDespatchMixin,
    PDFMixin,
    GoogleMapMixin
  ],
  components: {
    ModalDialog,
    DocumentList,
    ProgressTracker,
    ProgressTrackerItem,
    ProgressTrackerButton,
    ProgressTrackerPanel,
    TaskItemList,
    TaskItemScannedList,
    TaskAlertList,
    TaskManagerMapView,
    MapMarker,
    MapPolyline,
    TaskMapRoute,
    TaskItemPopover,
    MapRouteEventPopover,
    MapTaskItemMarker,
    MapTaskEndpointMarker,
    MapTaskRouteEventMarker,
    MapTaskEndpointPopover,
    LabelPanelStatusBadge,
    TaskProgressPreview,
    TaskLoadingSummary,
    TaskApprovalBar
  },
  data() {
    return {
      taskId: null,
      task: {
        taskType: {}
      },
      taskPendingPanels: [],
      taskItemScannedItemList: [],

      taskItemList: {
        sortOrder: [],
        editing: false,
        submitting: false,
        form: {}
      },
    }
  },
  created() {
    this.LinkTypes = LinkTypes
    this.TaskApprovalTypes = TaskApprovalTypes
    this.TaskStatusTypes = TaskStatusTypes
    this.TaskStatusTypeLookup = TaskStatusTypeLookup
    this.ScannedItemTypes = ScannedItemTypes
    this.ScannedItemTypeLookup = ScannedItemTypeLookup
  },
  mounted() {
    this.$data.taskId = this.$route.params.id

    this.throttledMapUpdateFunc = _.throttle(this.mapDataUpdated, 5000)

    // Subscribe to all relevant endpoints
    this.subscribeEndpoint(`/tasks/${this.$data.taskId}`, () => {
      this.reloadData()
    })

    this.requestData()
  },
  beforeRouteUpdate (to, from, next) {
    next()

    this.unsubscribeEndpoint(`/tasks/${this.$data.taskId}`)
    this.unsubscribeEndpoint(`/tasks/${this.$data.taskId}/map`)
    this.$data.taskId = this.$route.params.id

    this.subscribeEndpoint(`/tasks/${this.$data.taskId}`, () => {
      this.requestData()
    })
    this.subscribeEndpoint(`/tasks/${this.$data.taskId}/map`, () => {
      this.mapDataUpdated()
    })

    this.pageRefreshed()
    this.requestData()
  },
  beforeDestroy() {
    this.unsubscribeEndpoint(`/tasks/${this.$data.taskId}`)
    this.unsubscribeEndpoint(`/tasks/${this.$data.taskId}/map`)
  },
  computed: {
    containsMappingData() {
      // Set if task items have location
      if(!this.task || !this.task.taskItems)
        return false

      return this.task.taskItems.find(item => item.isLocationEnabled && (item.locationAddress||'').length > 0) != null
    },
    isRouteStartMatchEnd() {
      if(!this.task)
        return false
      if(!this.task.routeStartLocationCoordinate || !this.task.routeEndLocationCoordinate)
        return false

      var minDistance = 100
      var startEndDist = MapUtils.calculateDistanceBetweenPoints(this.task.routeStartLocationCoordinate.lng, this.task.routeStartLocationCoordinate.lat, this.task.routeEndLocationCoordinate.lng, this.task.routeEndLocationCoordinate.lat)
      return startEndDist <= minDistance
    },
    taskUserMapFilter() {
      if(!this.task || !this.task.users)
        return []

      // If task has a primary user, only use that
      if(this.task.primaryUserId)
        return [this.task.primaryUserId]

      return this.task.users.map(user => user.id)
    },
    activityStreamURL() {
      if(!this.$data.taskId)
        return null
      return `/tasks/${this.$data.taskId}/activitystream`
    },
    avatarImage() {
      if(this.isTaskLocationTracking)
        return '/glass/img/tasks-overview-delivery.png'
      if(this.isTaskDespatchEnabled)
        return '/glass/img/tasks-overview-pickup.png'

      return '/glass/img/tasks-overview-task.png'
    },
    isTaskPendingPanels() {
      return this.task.status == TaskStatusTypes.pendingProduction
    },
    isTaskPendingApproval() {
      return this.task.status == TaskStatusTypes.pendingApproval
    },
    isTaskLoading() {
      return this.task.status == TaskStatusTypes.loading
    },
    isTaskReady() {
      return this.task.status == TaskStatusTypes.ready
    },
    isTaskInProgress() {
      return this.task.status == TaskStatusTypes.inProgress
    },
    isTaskCompleted() {
      return this.task.status == TaskStatusTypes.completed
    },
    isTaskCancelled() {
      return this.task.status == TaskStatusTypes.cancelled
    },
    isTaskLocationTracking() {
      if(!this.task.taskType)
        return false
      return this.task.taskType.isLocationTrackingEnabled
    },
    isTaskDespatchEnabled() {
      if(!this.task.taskType)
        return false
      return this.task.taskType.isDespatchEnabled && this.task.taskType.isOrderItemDespatchEnabled
    },
    isTaskLoadingNotesRequired() {
      if(!this.task.taskType)
        return false
      return this.task.taskType.isLoadingNotesEnabled
    },
    containsTaskItems() {
      if(!this.task.taskItems)
        return false
      return this.task.taskItems.length > 0
    },
    taskScannedItemList() {
      if(!this.task.taskItems)
        return []

      let outputScannedItemList = this.task.taskItems.reduce((curValue, taskItem) => {
        let scannedTaskItem = this.taskItemScannedItemList.find(scanTaskItem => scanTaskItem.id == taskItem.id)
        if(scannedTaskItem)
          return _.concat(curValue, scannedTaskItem.taskItemScannedItems || [])
        return curValue
      }, [])

      return outputScannedItemList
    },
    requiresAccountsApproval() {
      return this.task.requiresAccountsApproval
    },
    canSkipPendingPanels() {
      if(!this.userHasUpdatePermission(this.PermissionTypes.TaskPermissionTypes.task))
        return false
      return this.isTaskPendingPanels
    },
    canApproveTask() {
      return this.userHasUpdatePermission(this.PermissionTypes.TaskPermissionTypes.taskManager)
    },
    canEditTask() {
      if(!this.userHasUpdatePermission(this.PermissionTypes.TaskPermissionTypes.task))
        return false
      return this.isTaskLoading || this.isTaskPendingApproval || this.isTaskReady || this.isTaskInProgress || this.isTaskPendingPanels
    },
    canEditTaskItems() {
      if(!this.userHasUpdatePermission(this.PermissionTypes.TaskPermissionTypes.task))
        return false
      return this.isTaskLoading || this.isTaskPendingApproval || this.isTaskReady || this.isTaskInProgress || this.isTaskPendingPanels
    },
    canCancelTask() {
      if(!this.userHasUpdatePermission(this.PermissionTypes.TaskPermissionTypes.task))
        return false
      return this.isTaskLoading || this.isTaskPendingApproval || this.isTaskReady || this.isTaskInProgress || this.isTaskPendingPanels
    },
    canCompleteTask() {
      if(!this.userHasUpdatePermission(this.PermissionTypes.TaskPermissionTypes.task))
        return false
      return this.isTaskLoading || this.isTaskReady || this.isTaskInProgress || this.isTaskPendingPanels
    },
    canPrintRunSheet() {
      if(this.isTaskPendingApproval)
        return false
      return this.containsTaskItems
    },
    canPrintCustomerSummarySheet() {
      if(this.isTaskPendingApproval)
        return false

      if(!this.task.taskType)
        return false
      return true
    },
    canRecheckApprovals() {
      return this.isTaskPendingApproval && this.userHasUpdatePermission(this.PermissionTypes.TaskPermissionTypes.task)
    },
    startLocationDescription() {
      switch(this.task.routeStartLocationType) {
        case TaskRouteLocationTypes.accountLocation:
          return this.task.startPointAccountLocation ? `${this.task.startPointAccountLocation.name} - ${this.task.startPointAccountLocation.formattedAddress}` : ''
        case TaskRouteLocationTypes.currentUserLocation:
          return 'Current User Location'
        case TaskRouteLocationTypes.address:
          return this.task.routeStartLocationAddress
        default:
          return 'None'
      }
    },
    endLocationDescription() {
      switch(this.task.routeEndLocationType) {
        case TaskRouteLocationTypes.accountLocation:
          return this.task.endPointAccountLocation ? `${this.task.endPointAccountLocation.name} - ${this.task.endPointAccountLocation.formattedAddress}` : ''
        case TaskRouteLocationTypes.currentUserLocation:
          return 'Current User Location'
        case TaskRouteLocationTypes.address:
          return this.task.routeEndLocationAddress
        default:
          return 'None'
      }
    }
  },
  methods: {
    reloadData() {
      if(this.$refs.activityStream)
        this.$refs.activityStream.reloadData()

      this.requestData()
    },
    requestData() {
      this.pageLoadData(Promise.all([
        this.$apiService.tasksService.getTask(this.$data.taskId),
        this.$apiService.tasksService.getTaskMapData(this.$data.taskId),
        this.$apiService.tasksService.getTaskPendingPanels(this.$data.taskId),
        this.$apiService.tasksService.getTaskScannedItems(this.$data.taskId)
      ]))
      .then(results => {
        this.task = results[0].data
        
        _.merge(this.task, _.pick(results[1].data, [
          'routeStartCoordinate', 'routePolylineCode', 'routePoints', 'routeKeyPoints', 'routeDuration', 'routeDistance',
          'routeStartLocationType', 'routeStartLocationAddress', 'routeEndLocationType', 'routeEndLocationAddress'
        ]))
        this.updateTaskUserRoutes(results[1].data.taskUserRoutes)

        this.taskPendingPanels = results[2].data

        this.taskItemScannedItemList = results[3].data
      })
      .catch(err => this.handlePageLoadError(err))
    },

    // Map displayed
    onMapDisplayed() {
      // Fit to the route
      this.$nextTick(() => {
        if(!this.task || !this.task.routePoints)
          return

        if(this.task.routePoints.length >= 2) {
          if(this.$refs.mapView)
            this.$refs.mapView.panToFitPointList(this.task.routePoints)
        }
      })
    },

    displayPendingPanelsTab() {
      this.$refs.pendingPanelsTab.show()
    },
    skipPendingPanels() {
      this.confirmMessage('Skip Pending Production?', `Skip the pending production item status and move the task straight to ready?`)
      .then(success => {
        if(success) {
          this.$apiService.tasksService.skipTaskPendingProduction(this.task.id)
          .then(() => {
            this.successToast(`Task updated`)
            this.reloadData()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Failed to skip pending production status')
          })
        }
      })
    },
    cancelTask() {
      this.displayModal(CancelTaskDialog, {
        taskId: parseInt(this.task.id),
        isTaskCancel: true
      })
      .then(success => {
        if(success) {
          this.successToast('Task Cancelled')
          this.reloadData()
        }
      })
    },
    completeTask() {
      this.confirmMessage('Close Task?', '', { html: 'This will close the task and only flag the task as completed.<br />It will <strong>not</strong> mark despatch items as despatched and <strong>no</strong> invoices will be generated' })
      .then(success => {
        if(success) {
          this.$apiService.tasksService.closeTask(this.task.id)
          .then(() => {
            this.successToast(`Task Closed`)
            this.reloadData()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Failed to close task')
          })
        }
      })
    },
    editTask() {
      this.displayModal(TaskEditDialog, { 
        taskId: parseInt(this.task.id),
      })
      .then((success) => {
        if(success) {
          this.successToast('Task Order Updated')
          this.reloadData()
        }
      })
    },
    editTaskItemOrder() {
      this.taskItemList.editing = true
    },
    submitTaskItemListEditing() {
      this.taskItemList.submitting = true

      var taskItemBatchData = this.taskItemList.sortOrder.map((taskItemId, index) => {
        return {
          id: taskItemId,
          sortOrder: index
        }
      })

      this.$apiService.tasksService.updateTaskItemBatch(this.task.id, { taskItems: taskItemBatchData })
      .then(response => {
        this.taskItemList.editing = false
        this.taskItemList.submitting = false
        this.reloadData()
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to update', 'Error while attempting to update task item order')                
      })
      .finally(() => {
        this.taskItemList.submitting = false
      })
    },
    cancelTaskItemListEditing() {
      this.taskItemList.editing = false
      this.taskItemList.submitting = false

      this.reloadData()
    },
    addTaskItemStoppage() {
      this.displayModal(TaskItemEditDialog, { 
        taskId: parseInt(this.task.id),
        isCreateMode: true
      }, {
        type: TaskItemTypes.stoppage,
        name: 'Break',
        details: '',
        estimatedProcessingTime: 30,      // Default 30min break
        isLocationEnabled: false
      })
      .then((success) => {
        if(success) {
          this.successToast('Task Item Added')
          this.reloadData()
        }
      })
    },
    addTaskItemGeneralItem() {
      this.displayModal(TaskItemEditDialog, { 
        taskId: parseInt(this.task.id),
        isCreateMode: true
      }, {
        type: TaskItemTypes.general,
        name: 'Misc',
        details: '',
        estimatedProcessingTime: 30,      // Default 30min break
        isLocationEnabled: this.isTaskLocationTracking
      })
      .then((success) => {
        if(success) {
          this.successToast('Task Item Added')
          this.reloadData()
        }
      })
    },
    cancelRunsheetItem(taskItemId) {
      this.displayModal(CancelTaskDialog, {
        taskId: parseInt(this.task.id),
        taskItemId: taskItemId,
        isTaskItemCancel: true
      })
      .then(success => {
        if(success) {
          this.successToast('Task Item Cancelled')
          this.reloadData()
        }
      })
    },
    editRunsheetItem(taskItemId) {
      this.displayModal(TaskItemEditDialog, { 
        taskId: parseInt(this.task.id),
        taskItemId: taskItemId,
      })
      .then((success) => {
        if(success) {
          this.successToast('Task Item Updated')
          this.reloadData()
        }
      })
    },
    completeTaskItem(taskItemId) {
      this.displayModal(TaskItemCompleteDialog, { 
        task: this.task,
        taskId: parseInt(this.task.id),
        taskItemId: parseInt(taskItemId),
      })
      .then((success) => {
        if(success) {
          this.successToast('Task Item Completed')
          this.reloadData()
        }
      })
    },

    printRunSheet(options = {}) {
      var params = $.param(options)
      this.displayReportPDF(`/tasks/${this.task.id}/runsheet?${params}`)
    },
    printPendingPanelSheet(options = {}) {
      var params = $.param(options)
      this.displayReportPDF(`/tasks/${this.task.id}/pendingpanels?${params}`)
    },
    printCustomerSummarySheet(options = {}) {
      var params = $.param(options)
      this.displayReportPDF(`/tasks/${this.task.id}/customersummary?${params}`)
    },

    onMapTaskItemClicked(taskItem, evt) {
      let mapElement = this.getElementFromClickEvent(evt)

      if(mapElement)
        this.$refs.mapTaskItemPopover.displayFromElement(mapElement, this.task, taskItem)
    },
    onMapRouteEventClicked(routeEvent, taskRoute, evt) {
      let mapElement = this.getElementFromClickEvent(evt)

      if(mapElement)
        this.$refs.mapRouteEventPopover.displayFromElement(mapElement, routeEvent, taskRoute, this.task)
    },
    onMapTaskStartClicked(evt) {
      let mapElement = this.getElementFromClickEvent(evt)

      if(mapElement)
        this.$refs.mapTaskEndpointPopover.displayFromElement(mapElement, this.task)
    },
    onMapTaskEndClicked(evt) {
      let mapElement = this.getElementFromClickEvent(evt)

      if(mapElement)
        this.$refs.mapTaskEndpointPopover.displayFromElement(mapElement, this.task)
    },
    mapDataUpdated() {
      // Reload task map data
      this.$apiService.tasksService.getTaskMapData(this.$data.taskId)
      .then(response => {

        // Merge in mapping data to the task itself
        this.updateTaskUserRoutes(response.data.taskUserRoutes)
      })
    },
    userLocationUpdated() {
      // Note: Only update map data every 5 seconds, not everytime the user locations are updated
      this.throttledMapUpdateFunc()
    },
    updateTaskUserRoutes(userRouteList) {
      this.task.taskUserRoutes = (userRouteList || []).map(route => {
        let user = this.task.users.find(user => user.id == route.userId)
        let routeUserEvents = route.taskUserRouteEvents || []
        
        return {
          id: route.id,
          routePolylineCode: route.routePolylineCode,
          highlightColour: user ? user.highlightColour : '#808080',
          user: user,
          routeUserEvents: routeUserEvents
        }
      })
    },

    approveTask(approvalType, title) {

      this.displayModal(GeneralActionDialog, { 
        title: `Approve ${title}`,
        icon: 'fa-thumbs-up',
        submitTitle: `Approve ${title}`,
        textLabel: 'Approval Note',
        placeholder: `Notes for approving the ${title} section of the task`,
        textField: 'approvedNote',
        submitPromise: (data) => {
          return this.$apiService.tasksService.approveTask(this.$data.task.id, { approvalType: approvalType, approvedNote: data.text, uploadFileList: data.uploadFileList })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`${title} approved`)
          this.reloadData()
        }
      })
    },
    denyTask(approvalType, title) {
      this.displayModal(GeneralActionDialog, { 
        title: `Deny ${title}`,
        icon: 'fa-thumbs-down',
        submitTitle: `Deny ${title}`,
        textLabel: 'Denied Reason',
        placeholder: `Reason for denying the ${title} section of the task`,
        textField: 'deniedReason',
        submitPromise: (data) => {
          return this.$apiService.tasksService.denyTask(this.$data.task.id, { approvalType: approvalType, deniedReason: data.text, uploadFileList: data.uploadFileList })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`${title} denied`)
          this.reloadData()
        }
      })
    },
    recheckApprovals() {
      let taskView = this

      this.displayProcessingDialog({
        title: 'Rechecking Approvals',
        message: 'Checking all linked items to the task',
        icon: 'fa-sync',
        onBeforeOpen: async (instance) => {
          try {
            await taskView.$apiService.tasksService.recheckTaskApprovals(this.taskId)

            taskView.reloadData()

            instance.hide()

          } catch(err) {
            instance.hide()
            taskView.displayNetworkError(err, 'Unable to recheck approvals')
          }
        }
      })
    }

    

  }
}
</script>